import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { dateTime, money, moneyShort, personName, time } from "@/lib/format";
import { Badge, Pager, Select, cx, usePaged } from "./ui";
import { td, th } from "./Layout";
import { periodRange } from "./Insights";

export const EXTRA_TABS = { "Cash transfers": "bank", Customers: "customers", "Discounts & refunds": "giving", "Stock changes": "stock" } as const;
export type ExtraTab = keyof typeof EXTRA_TABS;

type Deposit = { id: string; destination: string; account: string | null; at: number; shop: string | null; amount: number; slip: string | null; taker: string | null; by: string; checkedBy: string | null; reason: string | null };
type Bank = { totals: { cashSales: number; counted: number; sent: number; byDestination?: Record<string, number>; reached: number; notChecked: number; inSafe: number }; deposits: Deposit[] };
type Customer = { key: string; name: string; phone: string | null; group: string; sales: number; total: number; last: number; owes: number };
type Customers = { totals: { customers: number; biggest: Customer | null; total: number; wholesale: number }; customers: Customer[] };
type GiveRow = { id: string; at: number; number: string; teller: string; customer: string | null; type: string; why: string; approvedBy: string | null; amount: number };
type Giving = { totals: { discounts: number; discountCount: number; refunded: number; refundCount: number; overrides: number; total: number }; rows: GiveRow[] };
type Sum = { units: number; value: number };
type MoveRow = { id: string; at: number; item: string; kind: string; qty: number; note: string | null; ref: string | null; by: string | null; value: number };
type Stock = { totals: { delivered: Sum; lostInCounts: Sum; foundInCounts: Sum; writtenOff: Sum }; moves: MoveRow[] };

const KIND: Record<string, string> = { receive: "Delivery", stocktake: "Stock count", adjust: "Correction", writeoff: "Written off", rma: "Sent back", transfer_in: "From website", transfer_out: "To website" };
const DEST: Record<string, string> = { bank: "Bank", momo: "Mobile Money", owner: "Owner", other: "Other" };
const hasRef = (d: Deposit) => !!d.slip || d.destination === "owner" || d.destination === "other";
const statusOf = (d: Deposit) => (d.checkedBy ? "Arrived" : hasRef(d) ? "Not checked yet" : "No reference");
const day = (t: number) => new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const units = (n: number) => `${n.toLocaleString("en-GB")} unit${n === 1 ? "" : "s"}`;

function url(tab: ExtraTab, period: string, shop: string) {
  const r = periodRange(period);
  return till(`/admin/reports/extras/${EXTRA_TABS[tab]}?from=${r.from}&to=${r.to}&shop=${encodeURIComponent(shop)}`);
}

/** The rows for the Excel download of one of these tabs. */
export async function extraCsv(tab: ExtraTab, period: string, shop: string): Promise<(string | number | null)[][]> {
  const u = url(tab, period, shop);
  if (tab === "Cash transfers") {
    const d = await api<Bank>(u);
    return [["Date", "Shop", "Sent to", "Account", "Amount", "Reference", "Taken by", "Recorded by", "Checked by", "Status", "Reason"], ...d.deposits.map((x) => [new Date(x.at).toISOString(), x.shop, DEST[x.destination] ?? x.destination, x.account, x.amount / 100, x.slip, personName(x.taker), personName(x.by), x.checkedBy ? personName(x.checkedBy) : null, statusOf(x), x.reason])];
  }
  if (tab === "Customers") {
    const d = await api<Customers>(u);
    return [["Customer", "Phone", "Group", "Sales", "Total spent", "Last bought", "Owes now"], ...d.customers.map((x) => [x.name, x.phone, x.group, x.sales, x.total / 100, new Date(x.last).toISOString(), x.owes / 100])];
  }
  if (tab === "Discounts & refunds") {
    const d = await api<Giving>(u);
    return [["Date", "Receipt", "Teller", "Customer", "Type", "Why", "Approved by", "Amount"], ...d.rows.map((x) => [new Date(x.at).toISOString(), x.number, personName(x.teller), x.customer, x.type, x.why, x.approvedBy ? personName(x.approvedBy) : null, x.amount / 100])];
  }
  const d = await api<Stock>(u);
  return [["Date", "Product", "What", "Change", "Value at cost", "Reference", "Note", "By"], ...d.moves.map((x) => [new Date(x.at).toISOString(), x.item, KIND[x.kind] ?? x.kind, x.qty, x.value / 100, x.ref, x.note, personName(x.by)])];
}

function Tiles({ items }: { items: [string, string, string][] }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 px-4 pt-4 lg:grid-cols-3">
      {items.map(([l, v, n]) => (
        <div key={l} className="min-w-0 rounded-xl bg-slate-50 px-3.5 py-3">
          <span className="text-xs font-semibold text-slate-500">{l}</span>
          <b className="mt-0.5 block truncate text-lg font-extrabold tracking-tight">{v}</b>
          <span className="block truncate text-xs text-slate-500">{n}</span>
        </div>
      ))}
    </div>
  );
}
const Empty = ({ children }: { children: React.ReactNode }) => <p className="px-5 py-8 text-center text-sm text-slate-500">{children}</p>;
const Foot = ({ children }: { children: React.ReactNode }) => <p className="px-5 py-3 text-xs text-slate-500">{children}</p>;

function BankTab({ d }: { d: Bank }) {
  const [status, setStatus] = useState("");
  const [dest, setDest] = useState("");
  const list = d.deposits.filter((x) => (!status || statusOf(x) === status) && (!dest || x.destination === dest));
  const paged = usePaged(list);
  const t = d.totals;
  return (
    <>
      <Tiles items={[["Cash sales", moneyShort(t.cashSales), "Taken at the tills"], ["Counted into the safe", moneyShort(t.counted), "Received from tellers"], ["To bank", moneyShort(t.byDestination?.bank ?? 0), "Sent to bank accounts"], ["To Mobile Money", moneyShort(t.byDestination?.momo ?? 0), "Sent to MoMo numbers"], ["To the owner", moneyShort(t.byDestination?.owner ?? 0), "Handed over"], ["Other", moneyShort(t.byDestination?.other ?? 0), "Anything else"], ["Arrived", moneyShort(t.reached), "Sent and checked"], ["Not checked yet", moneyShort(t.notChecked), "Check the statement or MoMo history"], ["In the safe now", moneyShort(t.inSafe), "Today, whatever the period"]]} />
      <div className="flex flex-wrap justify-end gap-2 px-4 pt-3">
        <Select aria-label="Sent to" value={dest} onChange={(e) => setDest(e.target.value)} className="!h-10 !w-48">
          <option value="">Everywhere</option>
          {Object.entries(DEST).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Select>
        <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} className="!h-10 !w-48">
          <option value="">Any status</option>
          <option>Arrived</option>
          <option>Not checked yet</option>
          <option>No reference</option>
        </Select>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr>{["Date", "Shop", "Sent to", "Amount", "Reference", "Taken by", "Checked by", "Status"].map((h, i) => <th key={h} className={cx(th, i === 3 && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {paged.rows.map((x) => (
              <tr key={x.id}>
                <td className={td}><b className="block font-semibold">{day(x.at)}</b><span className="text-xs text-slate-500">{time(x.at)}</span></td>
                <td className={cx(td, "text-[13px]")}>{x.shop ?? "-"}</td>
                <td className={cx(td, "text-[13px]")}><b className="block font-semibold">{DEST[x.destination] ?? x.destination}</b>{x.account ? <span className="text-xs text-slate-500">{x.account}</span> : null}</td>
                <td className={cx(td, "text-right font-bold")}>{money(x.amount)}</td>
                <td className={cx(td, "text-[13px]")}>{x.slip ?? <span className="text-slate-500">{x.reason ?? (hasRef(x) ? "Not needed" : "None yet")}</span>}</td>
                <td className={cx(td, "text-[13px]")}>{personName(x.taker)}</td>
                <td className={cx(td, "text-[13px]")}>{x.checkedBy ? personName(x.checkedBy) : "-"}</td>
                <td className={td}><Badge tone={x.checkedBy ? "brand" : hasRef(x) ? "slate" : "amber"}>{statusOf(x)}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!list.length ? <Empty>No cash transfers here in this period.</Empty> : null}
      <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
      <Foot>Cash taken in one period can be transferred in the next, so these totals may not match exactly.</Foot>
    </>
  );
}

function CustomersTab({ d }: { d: Customers }) {
  const paged = usePaged(d.customers);
  const t = d.totals;
  const share = t.total ? Math.round((t.wholesale / t.total) * 100) : 0;
  return (
    <>
      <Tiles items={[["Customers who bought", t.customers.toLocaleString("en-GB"), "In this period"], ["Biggest customer", t.biggest ? moneyShort(t.biggest.total) : moneyShort(0), t.biggest?.name ?? "None yet"], ["Retail / wholesale", t.total ? `${100 - share}% / ${share}%` : "-", "Share of total sales"]]} />
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr>{["Customer", "Group", "Sales", "Total spent", "Last bought", "Owes now"].map((h, i) => <th key={h} className={cx(th, i >= 2 && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {paged.rows.map((x) => (
              <tr key={x.key}>
                <td className={td}><b className="block font-semibold">{x.name}</b>{x.phone ? <span className="text-xs text-slate-500">{x.phone}</span> : null}</td>
                <td className={cx(td, "text-[13px] capitalize")}>{x.group}</td>
                <td className={cx(td, "text-right")}>{x.sales}</td>
                <td className={cx(td, "text-right font-bold")}>{moneyShort(x.total)}</td>
                <td className={cx(td, "text-right text-[13px]")}>{day(x.last)}</td>
                <td className={cx(td, "text-right", x.owes > 0 && "font-bold")}>{x.owes > 0 ? moneyShort(x.owes) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!d.customers.length ? <Empty>No customers bought in this period.</Empty> : null}
      <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
      <Foot>Owes now is today's total, across all shops.</Foot>
    </>
  );
}

function GivingTab({ d }: { d: Giving }) {
  const [type, setType] = useState("");
  const list = d.rows.filter((x) => !type || x.type === type);
  const paged = usePaged(list);
  const t = d.totals;
  return (
    <>
      <Tiles items={[["Discounts given", moneyShort(t.discounts), `${t.discountCount} sale${t.discountCount === 1 ? "" : "s"}`], ["Refunds", moneyShort(t.refunded), `${t.refundCount} refund${t.refundCount === 1 ? "" : "s"}`], ["Prices lowered by hand", moneyShort(t.overrides), "Below the normal price"]]} />
      <div className="flex justify-end px-4 pt-3">
        <Select aria-label="Type" value={type} onChange={(e) => setType(e.target.value)} className="!h-10 !w-48">
          <option value="">Everything</option>
          <option>Discount</option>
          <option>Refund</option>
          <option>Price changed</option>
        </Select>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr>{["Date", "Receipt", "Teller", "Customer", "Type", "Why", "Amount"].map((h, i) => <th key={h} className={cx(th, i === 6 && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {paged.rows.map((x) => (
              <tr key={x.id}>
                <td className={td} title={dateTime(x.at)}>{day(x.at)}</td>
                <td className={cx(td, "font-semibold")}>{x.number}</td>
                <td className={cx(td, "text-[13px]")}>{personName(x.teller)}{x.approvedBy ? <span className="block text-xs text-slate-500">Approved by {personName(x.approvedBy)}</span> : null}</td>
                <td className={cx(td, "max-w-[180px] truncate text-[13px]")}>{x.customer ?? "-"}</td>
                <td className={td}><Badge tone={x.type === "Discount" ? "brand" : x.type === "Refund" ? "amber" : "slate"}>{x.type}</Badge></td>
                <td className={cx(td, "max-w-[220px] whitespace-normal text-[13px]")}>{x.why}</td>
                <td className={cx(td, "text-right font-bold")}>{moneyShort(x.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!list.length ? <Empty>Nothing here in this period.</Empty> : null}
      <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
    </>
  );
}

function StockTab({ d }: { d: Stock }) {
  const paged = usePaged(d.moves);
  const t = d.totals;
  const line = (s: Sum) => (s.value ? `Worth ${moneyShort(s.value)} at cost` : "No cost value");
  return (
    <>
      <Tiles items={[["Delivered in", units(t.delivered.units), line(t.delivered)], ["Lost in counts", units(t.lostInCounts.units), line(t.lostInCounts)], ["Found in counts", units(t.foundInCounts.units), line(t.foundInCounts)], ["Written off", units(t.writtenOff.units), line(t.writtenOff)]]} />
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr>{["Date", "Product", "What", "Change", "Note", "By"].map((h, i) => <th key={h} className={cx(th, i === 3 && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {paged.rows.map((x) => (
              <tr key={x.id}>
                <td className={td} title={dateTime(x.at)}>{day(x.at)}</td>
                <td className={cx(td, "whitespace-normal font-semibold")}>{x.item}</td>
                <td className={td}><Badge tone={x.kind === "receive" ? "brand" : x.kind === "writeoff" || x.kind === "rma" ? "amber" : "slate"}>{KIND[x.kind] ?? x.kind}</Badge></td>
                <td className={cx(td, "text-right font-bold")}>{x.qty > 0 ? "+" : ""}{x.qty.toLocaleString("en-GB")}</td>
                <td className={cx(td, "max-w-[240px] whitespace-normal text-[13px] text-slate-500")}>{[x.ref, x.note].filter(Boolean).join(" · ") || "-"}</td>
                <td className={cx(td, "text-[13px]")}>{personName(x.by)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!d.moves.length ? <Empty>No deliveries or stock changes in this period.</Empty> : null}
      <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
      <Foot>Normal sales and returns aren't listed here. The full history is on Receive & count stock.</Foot>
    </>
  );
}

export function ReportExtra({ tab, period, shop }: { tab: ExtraTab; period: string; shop: string }) {
  const q = useQuery({ queryKey: ["report-extra", tab, period, shop], queryFn: () => api<unknown>(url(tab, period, shop)), placeholderData: keepPreviousData });
  if (q.isError) return <Empty>{q.error instanceof Error ? q.error.message : "Couldn't load this report"}</Empty>;
  if (!q.data || q.isPlaceholderData) return <Empty>Loading…</Empty>;
  if (tab === "Cash transfers") return <BankTab d={q.data as Bank} />;
  if (tab === "Customers") return <CustomersTab d={q.data as Customers} />;
  if (tab === "Discounts & refunds") return <GivingTab d={q.data as Giving} />;
  return <StockTab d={q.data as Stock} />;
}
